'use client';

// Based on https://www.npmjs.com/package/react-loading-skeleton

/* eslint-disable react/no-array-index-key */
import { checkIsNative, cn, Div, LinearGradient } from '@scatterlab/zeta-core';
import {
  CSSProperties,
  FunctionComponent,
  memo,
  PropsWithChildren,
  ReactElement,
  use,
  useMemo,
} from 'react';

import { SkeletonThemeContext } from './SkeletonThemeContext';
import { SkeletonStyleProps } from './types';

// For performance & cleanliness, don't add any inline styles unless we have to
function styleOptionsToCssProperties({
  baseColor,
  highlightColor,

  width,
  height,
  borderRadius,
  circle,

  duration,
}: SkeletonStyleProps & { circle: boolean }): [
  CSSProperties,
  {
    duration?: string;
    baseColor?: string;
    highlightColor?: string;
  },
] {
  const style: CSSProperties = {};
  const customStyle: {
    duration?: string;
    baseColor?: string;
    highlightColor?: string;
  } = {};

  if (typeof duration === 'number') customStyle.duration = `${duration}s`;

  if (typeof width === 'string' || typeof width === 'number')
    style.width = width;
  if (typeof height === 'string' || typeof height === 'number')
    style.height = height;

  if (typeof borderRadius === 'string' || typeof borderRadius === 'number')
    style.borderRadius = borderRadius;

  if (circle) style.borderRadius = '50%';

  if (typeof baseColor !== 'undefined') customStyle.baseColor = baseColor;
  if (typeof highlightColor !== 'undefined')
    customStyle.highlightColor = highlightColor;

  return [style, customStyle];
}

export interface SkeletonProps extends SkeletonStyleProps {
  count?: number;
  wrapper?: FunctionComponent<PropsWithChildren>;

  className?: string;
  containerClassName?: string;

  circle?: boolean;
  style?: CSSProperties;
}

const Skeleton = ({
  count = 1,
  wrapper: Wrapper,

  className: customClassName,
  containerClassName,

  circle = false,

  style: styleProp,
  ...originalPropsStyleOptions
}: SkeletonProps): ReactElement => {
  const contextStyleOptions = use(SkeletonThemeContext);

  const [style, customStyle] = useMemo(() => {
    const propsStyleOptions = { ...originalPropsStyleOptions };

    // DO NOT overwrite style options from the context if `propsStyleOptions`
    // has properties explicity set to undefined
    for (const [key, value] of Object.entries(originalPropsStyleOptions)) {
      if (typeof value === 'undefined') {
        delete propsStyleOptions[key as keyof typeof propsStyleOptions];
      }
    }

    // Props take priority over context
    const styleOptions = {
      ...contextStyleOptions,
      ...propsStyleOptions,
      circle,
    };

    // `styleProp` has the least priority out of everything
    const [styleFromOptions, customStyleFromOptions] =
      styleOptionsToCssProperties(styleOptions);

    return [
      {
        ...styleProp,
        ...styleFromOptions,
      },
      customStyleFromOptions,
    ];
  }, [originalPropsStyleOptions, contextStyleOptions, circle, styleProp]);

  const countCeil = Math.ceil(count);

  const children = useMemo(() => {
    const elements: ReactElement[] = [];
    for (let i = 0; i < countCeil; i += 1) {
      let thisStyle = style;

      if (countCeil > count && i === countCeil - 1) {
        // count is not an integer and we've reached the last iteration of
        // the loop, so add a "fractional" skeleton.
        //
        // For example, if count is 3.5, we've already added 3 full
        // skeletons, so now we add one more skeleton that is 0.5 times the
        // original width.

        const width = thisStyle.width ?? '100%'; // 100% is the default since that's what's in the CSS

        const fractionalPart = count % 1;

        const fractionalWidth =
          typeof width === 'number'
            ? width * fractionalPart
            : `calc(${width} * ${fractionalPart})`;

        thisStyle = { ...thisStyle, width: fractionalWidth };
      }

      const skeletonSpan = checkIsNative() ? (
        <Div
          className={cn(
            'relative inline-flex w-full select-none overflow-hidden rounded-md bg-gray-sub1 leading-[1]',
            customClassName,
          )}
          style={{
            ...thisStyle,
            background: customStyle.baseColor || '#262727',
          }}
          key={i}
        />
      ) : (
        <Div
          className={cn(
            'relative inline-flex w-full select-none overflow-hidden rounded-md bg-gray-sub1 leading-[1]',
            customClassName,
          )}
          style={{
            ...thisStyle,
            background: customStyle.baseColor || '#262727',
          }}
          key={i}
        >
          <Div
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className={cn(
              'absolute inset-x-0 top-0 flex h-full animate-[skeleton_1.5s_ease-in-out_infinite]',
              customStyle.duration &&
                `animate-[skeleton_${customStyle.duration}_ease-in-out_infinite]`,
            )}
            // @ts-expect-error - custom
            style={{
              ...(customStyle.duration && {
                '--tw-animation-duration': customStyle.duration,
              }),
            }}
          >
            <LinearGradient
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className={cn(
                'flex h-full flex-1 bg-gradient-to-r from-gray-sub1 via-[#2c2c2c] to-gray-sub1',
                customStyle.baseColor &&
                  `from-[${customStyle.baseColor}] to-[${customStyle.baseColor}]`,
                customStyle.highlightColor &&
                  `via-[${customStyle.highlightColor}]`,
              )}
              colors={[
                customStyle.baseColor || '#262727',
                customStyle.highlightColor || '#2c2c2c',
                customStyle.baseColor || '#262727',
              ]}
              // @ts-expect-error - custom
              style={{
                ...(customStyle.baseColor && {
                  '--tw-gradient-from': customStyle.baseColor,
                }),
                ...(customStyle.highlightColor && {
                  '--tw-gradient-to': customStyle.highlightColor,
                }),
              }}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
            />
          </Div>
        </Div>
      );

      elements.push(skeletonSpan);
    }
    return elements;
  }, [
    count,
    countCeil,
    customClassName,
    customStyle.baseColor,
    customStyle.duration,
    customStyle.highlightColor,
    style,
  ]);

  return (
    <Div
      className={cn('whitespace-pre-wrap leading-button', containerClassName)}
    >
      {Wrapper
        ? children.map((el, i) => <Wrapper key={i}>{el}</Wrapper>)
        : children}
    </Div>
  );
};

export default memo(Skeleton);
